import React, {
  useEffect, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import {
  buildExportFilename, exportRowsToCSV, exportTableToFile,
} from '../../../shared/utils/tableExport';
import ManageGroupsTable from './presentational/tables/ManageGroupsTable';
import AddGroupModal from './presentational/modals/AddGroupModal';
import ExportDataModal from './presentational/modals/ExportDataModal';
import ConfirmModal from './presentational/modals/ConfirmModal';
import { fetchGroups, deleteGroups } from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HiddenTable = styled.table`
  display: none;
`;

const ManageGroups = ({ backPath }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const groups = useSelector((state) => state.navigator.data.groups);
  const isDeletingGroups = useSelector((state) => state.navigator.ui.isDeletingGroups);

  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [visibleGroups, setVisibleGroups] = useState(groups);

  const deleteRef = useRef(null);
  if (groupToDelete) deleteRef.current = groupToDelete;
  const displayDelete = deleteRef.current;

  const bulkDeleteIdsRef = useRef([]);
  if (bulkSelectedIds.length > 0) bulkDeleteIdsRef.current = bulkSelectedIds;
  const displayBulkDeleteIds = bulkDeleteIdsRef.current;

  const refetchGroups = () => dispatch(fetchGroups(accountCode));

  const getGroupMessaging = (g) => (g.groupFilters?.messaging ? 'Yes' : 'No');
  const getGroupLeaderboard = (g) => (g.groupFilters?.leaderboard ? 'Yes' : 'No');
  const getGroupReporting = (g) => (g.groupFilters?.reporting ? 'Yes' : 'No');

  const exportToXLSX = () => {
    exportTableToFile('groups-export-table', buildExportFilename('groups', 'xlsx'), 'Groups');
    setIsExportOpen(false);
  };

  const exportToXLS = () => {
    exportTableToFile('groups-export-table', buildExportFilename('groups', 'xls'), 'Groups');
    setIsExportOpen(false);
  };

  const exportToCSV = () => {
    const headers = ['Group Name', 'Messaging', 'Leaderboard', 'Reporting', '# of Users'];
    const rows = visibleGroups.map((g) => [
      g.name || '',
      getGroupMessaging(g),
      getGroupLeaderboard(g),
      getGroupReporting(g),
      g.userCount ?? '',
    ]);
    exportRowsToCSV(headers, rows, buildExportFilename('groups', 'csv'));
    setIsExportOpen(false);
  };

  const handleBulkAction = (action, ids) => {
    setBulkAction(action);
    setBulkSelectedIds(ids);
  };

  const confirmBulkDelete = () => {
    dispatch(deleteGroups(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} group${bulkSelectedIds.length === 1 ? '' : 's'} deleted.`);
        refetchGroups();
        setBulkAction(null);
        setBulkSelectedIds([]);
      },
    }));
  };

  useEffect(() => {
    if (accountCode) {
      dispatch(fetchGroups(accountCode));
    }
  }, [accountCode, dispatch]);

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <BackLink type='button' onClick={() => history.push(backPath)}>
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Manage
          </BackLink>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Manage Groups
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Export'
            icon='download'
            iconColor='#1A1A1A'
            customBorderRadius='4px'
            borderColor='#D6D6D6'
            txtColor='#1A1A1A'
            bgColor='var(--theme-bg-surface, #FFF)'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsExportOpen(true)}
          />
          <Button
            name='Add Groups'
            icon='add-user'
            iconColor='#FFF'
            txtColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsAddGroupOpen(true)}
          />
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <ManageGroupsTable
          onEditGroup={setGroupToEdit}
          onDeleteGroup={setGroupToDelete}
          onBulkAction={handleBulkAction}
          onVisibleGroupsChange={setVisibleGroups}
        />
      </BottomSection>

      <AddGroupModal
        isOpen={isAddGroupOpen}
        onClose={() => setIsAddGroupOpen(false)}
      />

      <AddGroupModal
        isOpen={!!groupToEdit}
        onClose={() => setGroupToEdit(null)}
        group={groupToEdit}
        onUpdated={refetchGroups}
      />

      <ConfirmModal
        isOpen={!!groupToDelete}
        onClose={() => setGroupToDelete(null)}
        onConfirm={() => dispatch(deleteGroups(accountCode, [groupToDelete.id], {
          onSuccess: () => {
            showSuccessToast(`${groupToDelete.name} has been deleted.`);
            refetchGroups();
            setGroupToDelete(null);
          },
        }))}
        title='Delete Group'
        message={`Are you sure you want to delete ${displayDelete?.name}? This action cannot be undone.`}
        isLoading={isDeletingGroups}
      />

      <ConfirmModal
        isOpen={bulkAction === 'deleteGroups'}
        onClose={() => { setBulkAction(null); setBulkSelectedIds([]); }}
        onConfirm={confirmBulkDelete}
        title='Delete Groups'
        message={`Are you sure you want to delete ${displayBulkDeleteIds.length} group${displayBulkDeleteIds.length === 1 ? '' : 's'}? This action cannot be undone.`}
        isLoading={isDeletingGroups}
      />

      <ExportDataModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        onExportXLSX={exportToXLSX}
        onExportXLS={exportToXLS}
        onExportCSV={exportToCSV}
      />

      <HiddenTable id='groups-export-table'>
        <thead>
          <tr>
            <th>Group Name</th>
            <th>Messaging</th>
            <th>Leaderboard</th>
            <th>Reporting</th>
            <th># of Users</th>
          </tr>
        </thead>
        <tbody>
          {visibleGroups.map((g) => (
            <tr key={g.id}>
              <td>{g.name || ''}</td>
              <td>{getGroupMessaging(g)}</td>
              <td>{getGroupLeaderboard(g)}</td>
              <td>{getGroupReporting(g)}</td>
              <td>{g.userCount ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </HiddenTable>
    </Container>
  );
};

ManageGroups.propTypes = {
  backPath: PropTypes.string,
};

ManageGroups.defaultProps = {
  backPath: '/dashboard/manage',
};

export default ManageGroups;
